import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, Sparkles, MapPin, PartyPopper } from "lucide-react";
import { MaskedLine } from "./MaskedLine";
import { BouncyButton } from "./BouncyButton";

const Bunting = () => {
    const colors = ["#FF3B30", "#FFCC00", "#34C759", "#1E6FD9", "#B04DFF"];
    const flags = Array.from({ length: 24 });
    return (
        <svg viewBox="0 0 1200 70" className="pointer-events-none absolute left-0 top-0 w-full" preserveAspectRatio="none" aria-hidden="true">
            <path d="M0,8 Q600,70 1200,8" fill="none" stroke="#0F172A" strokeWidth="3" />
            {flags.map((_, i) => {
                const t = i / (flags.length - 1);
                const x = 20 + t * 1160;
                const y = 8 + Math.sin(Math.PI * t) * 26;
                return <polygon key={i} points={`${x},${y} ${x + 26},${y} ${x + 13},${y + 30}`} fill={colors[i % colors.length]} stroke="#0F172A" strokeWidth="2" />;
            })}
        </svg>
    );
};

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const photoY = useTransform(scrollYProgress, [0, 1], [0, 60]);
    const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

    return (
        <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-monster-cream pb-24 pt-36 sm:pt-40">
            <Bunting />
            <div className="pointer-events-none absolute -left-24 top-40 h-72 w-72 rounded-full bg-monster-sky" aria-hidden="true" />
            <div className="pointer-events-none absolute -right-28 bottom-10 h-80 w-80 rounded-full bg-[#E7F9EC]" aria-hidden="true" />

            <Star className="animate-floaty absolute left-[6%] top-[30%] hidden h-10 w-10 fill-monster-yellow text-monster-navy lg:block" style={{ "--float-rotate": "-12deg" }} aria-hidden="true" />
            <Sparkles className="animate-floaty absolute right-[8%] top-[22%] hidden h-9 w-9 text-monster-blue lg:block" style={{ animationDelay: "1.2s" }} aria-hidden="true" />
            <Star className="animate-floaty absolute bottom-[16%] left-[42%] hidden h-7 w-7 fill-monster-red text-monster-navy lg:block" style={{ animationDelay: "0.6s", "--float-rotate": "10deg" }} aria-hidden="true" />

            <div className="relative mx-auto grid max-w-6xl items-center gap-16 px-5 lg:grid-cols-[1.15fr_0.85fr]">
                <div>
                    <motion.button
                        data-testid="hero-launch-badge"
                        onClick={() => go("#launch-list")}
                        initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
                        animate={{ opacity: 1, scale: 1, rotate: -2 }}
                        transition={{ type: "spring", stiffness: 200, damping: 10, delay: 0.05 }}
                        className="mb-4 inline-flex items-center gap-2 rounded-2xl border-2 border-monster-navy bg-monster-yellow px-5 py-2.5 font-display text-sm font-semibold text-monster-navy shadow-sticker transition-transform hover:-translate-y-0.5 sm:text-base"
                    >
                        <PartyPopper className="h-5 w-5 shrink-0 text-monster-red" />
                        Opening Spring 2027 — join the launch list for 20% off
                    </motion.button>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.15 }}
                        className="mb-7 inline-flex items-center gap-2 rounded-full border-2 border-monster-navy bg-white px-4 py-2 font-display text-sm font-semibold text-monster-navy shadow-sticker-sm"
                    >
                        <MapPin className="h-4 w-4 text-monster-red" />
                        Serving Genesee County & surrounding areas
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.7, rotate: -4 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 120, damping: 13, delay: 0.3 }}
                        className="max-w-xl"
                    >
                        <img
                            src="/images/logo-transparent.png"
                            alt="Monsters Inflatables — bounce house rentals"
                            data-testid="hero-logo"
                            className="animate-floaty w-full object-contain drop-shadow-[10px_10px_0_rgba(15,23,42,0.9)]"
                        />
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.85, type: "spring", stiffness: 90, damping: 16 }}
                        className="mt-7 max-w-xl text-base text-monster-navy/75 md:text-lg"
                    >
                        Bounce houses, water slides, obstacle courses, and dunk tanks — delivered, set up, and sanitized.
                        You bring the kids, we bring the monster-sized fun.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1, type: "spring", stiffness: 90, damping: 16 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <BouncyButton testId="hero-book-btn" variant="blue" onClick={() => go("#book")}>
                            Book Your Bounce
                        </BouncyButton>
                        <BouncyButton testId="hero-lineup-btn" variant="white" onClick={() => go("#packages")}>
                            What We Offer
                        </BouncyButton>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.2 }}
                        className="mt-12 flex flex-wrap gap-8"
                    >
                        {[
                            ["6+", "Inflatables"],
                            ["Spring", "2027 launch"],
                            ["20%", "Off for early birds"],
                        ].map(([num, label]) => (
                            <div key={label}>
                                <div className="font-display text-3xl font-semibold text-monster-navy">{num}</div>
                                <div className="text-sm font-semibold uppercase tracking-wider text-monster-navy/50">{label}</div>
                            </div>
                        ))}
                    </motion.div>
                </div>

                <div className="relative mx-auto w-full max-w-md lg:max-w-none">
                    <motion.div style={{ y: photoY }} className="relative">
                        <motion.div
                            initial={{ opacity: 0, y: 60, rotate: 6 }}
                            animate={{ opacity: 1, y: 0, rotate: 3 }}
                            transition={{ type: "spring", stiffness: 70, damping: 14, delay: 0.5 }}
                            className="overflow-hidden rounded-[40px] border-2 border-monster-navy shadow-sticker-lg"
                        >
                            <img
                                src="https://images.unsplash.com/photo-1765947389722-2e96d8c0aad9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxib3VuY2UlMjBjYXN0bGV8ZW58MHx8fHwxNzgyMDc0MDc2fDA&ixlib=rb-4.1.0&q=85&w=900&h=1100&fit=crop"
                                alt="Red bounce house castle on a lawn"
                                className="h-[420px] w-full object-cover sm:h-[500px]"
                            />
                        </motion.div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 10, delay: 1.1 }}
                        className="absolute -right-3 -top-6 rotate-6 rounded-2xl border-2 border-monster-navy bg-monster-yellow px-4 py-2 font-display text-sm font-semibold text-monster-navy shadow-sticker-sm"
                    >
                        Spring 2027
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
